import React from 'react';
import { Box, Paper, Tab, Tabs } from '@mui/material';

export interface TabShellTab<T extends string> {
  key: T;
  label: React.ReactNode;
  icon?: React.ReactElement;
  disabled?: boolean;
}

interface TabShellProps<T extends string> {
  tabs: TabShellTab<T>[];
  value: T;
  onChange: (next: T) => void;
  /** Keep the tab bar pinned while the panel scrolls. */
  sticky?: boolean;
  children: React.ReactNode;
  'aria-label'?: string;
}

/**
 * The tab bar container pages share. System and Brands & Vendors carried the
 * same sticky Paper and the same Tabs styling byte for byte; pair this with
 * `useUrlTab` for the `?tab=` sync.
 */
export function TabShell<T extends string>({
  tabs,
  value,
  onChange,
  sticky = true,
  children,
  'aria-label': ariaLabel,
}: TabShellProps<T>) {
  return (
    <>
      <Paper
        elevation={0}
        sx={{
          position: sticky ? 'sticky' : 'static',
          top: 0,
          zIndex: 10,
          mb: 3,
          borderRadius: sticky ? 0 : undefined,
          borderLeft: sticky ? 0 : undefined,
          borderRight: sticky ? 0 : undefined,
          borderTop: sticky ? 0 : undefined,
          boxShadow: 'none',
        }}
      >
        <Tabs
          value={value}
          onChange={(_event, next: T) => onChange(next)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label={ariaLabel}
          sx={{
            px: 2,
            '& .MuiTab-root': {
              minHeight: 48,
              fontWeight: 500,
              fontSize: '0.9rem',
              color: 'text.secondary',
              '&.Mui-selected': { color: 'primary.main' },
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              disabled={tab.disabled}
              id={`tab-${tab.key}`}
              aria-controls={`tabpanel-${tab.key}`}
            />
          ))}
        </Tabs>
      </Paper>
      <Box role="tabpanel" id={`tabpanel-${value}`} aria-labelledby={`tab-${value}`}>
        {children}
      </Box>
    </>
  );
}
